import React from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import type { MapDriver } from './useDriverMapData';
import { useDeadReckoning } from './useDeadReckoning';
import { HtmlMapMarker } from '@components/Maps';
import BatteryIndicator from '@features/mapVisualization/components/BatteryIndicator';
import { getThumbnailUrl, gpuThumbnailCss } from '@utils/thumbnailUrl';

import { css, keyframes } from '@emotion/css';
import { Theme, useTheme, Tooltip, Typography } from '@mui/material';

dayjs.extend(relativeTime);

// TYPES --------------------------------------------------------------------- //

interface DriverStatusMarkerProps {
  mapId: string;
  driver: MapDriver;
  isSelected: boolean;
  onClick: () => void;
  /** Decoded polyline from the driver's active move — enables road-constrained dead reckoning */
  routePath?: { lat: number; lng: number }[] | null;
}

// TOP-DOWN CAR SVG ---------------------------------------------------------- //
// Points north (up) by default; rotated via CSS transform to match heading.

const CarIcon = ({ color, size, selected }: { color: string; size: number; selected?: boolean }) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 32'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    style={{ display: 'block' }}
  >
    {/* Body */}
    <rect
      x='3' y='2' width='18' height='28' rx='5'
      fill={color}
      stroke={selected ? '#fff' : 'none'}
      strokeWidth={selected ? 2 : 0}
    />
    {/* Windshield */}
    <rect x='5.5' y='5' width='13' height='7' rx='2.5' fill='rgba(255,255,255,0.35)' />
    {/* Rear window */}
    <rect x='5.5' y='21' width='13' height='5.5' rx='2.5' fill='rgba(255,255,255,0.2)' />
    {/* Side mirrors */}
    <rect x='0.5' y='7' width='3' height='2' rx='1' fill={color} />
    <rect x='20.5' y='7' width='3' height='2' rx='1' fill={color} />
  </svg>
);

// COMPONENT ----------------------------------------------------------------- //

const DriverStatusMarker = ({ mapId, driver, isSelected, onClick, routePath }: DriverStatusMarkerProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const isOnline = driver.status === 'online';
  const lastLocation = driver.gpslocations?.[0];

  const serverLat = lastLocation?.location?.coordinates?.[1];
  const serverLng = lastLocation?.location?.coordinates?.[0];
  const zIndex = isSelected ? 100 : isOnline ? 10 : 1;

  // Dead reckoning: extrapolate position between server updates
  // When a routePath is provided, the extrapolated position snaps to the road
  const { lat, lng } = useDeadReckoning({
    lat: serverLat ?? 0,
    lng: serverLng ?? 0,
    speed: lastLocation?.speed,
    heading: lastLocation?.heading,
    isMoving: lastLocation?.is_moving,
    timestamp: lastLocation?.time,
    routePath,
  });

  const isDriving = lastLocation?.is_moving === true && lastLocation?.heading != null;
  const headingDeg = lastLocation?.heading ?? 0;

  const displayName = driver.user?.display_name || 'Unknown';
  const avatarUrl = driver.user?.avatar_url;
  const lastPingTime = lastLocation?.time;
  const initials = displayName
    .split(' ')
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  if (serverLat == null || serverLng == null) return null;

  const markerColor = isOnline ? theme.palette.success.main : theme.palette.grey[400];

  return (
    <HtmlMapMarker mapId={mapId} position={{ lat, lng }} zIndex={zIndex}>
      <Tooltip
        title={
          <div className={cls.tooltipContent}>
            <div className={cls.tooltipHeader}>
              {avatarUrl ? (
                <img src={getThumbnailUrl(avatarUrl, 32)} alt={displayName} className={cls.tooltipAvatar} />
              ) : (
                <div className={cls.tooltipAvatarFallback}>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#fff', lineHeight: 1 }}>{initials}</Typography>
                </div>
              )}
              <div className={cls.tooltipInfo}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.2 }}>{displayName}</Typography>
                <div className={cls.tooltipStatusRow}>
                  <span
                    className={cls.tooltipStatusDot}
                    style={{ backgroundColor: isOnline ? '#22c55e' : '#9ca3af' }}
                  />
                  <Typography sx={{ fontSize: 10, opacity: 0.7 }}>
                    {isOnline ? 'Online' : 'Offline'}
                  </Typography>
                </div>
              </div>
            </div>
            <div className={cls.tooltipMeta}>
              {driver.moveCount > 0 ? (
                <Typography sx={{ fontSize: 10, opacity: 0.6 }}>
                  {driver.moveCount} move{driver.moveCount !== 1 ? 's' : ''}
                </Typography>
              ) : null}
              {lastLocation?.battery_level != null ? (
                <BatteryIndicator level={lastLocation.battery_level} charging={lastLocation.battery_is_charging} fontSize={10} iconSize={11} />
              ) : null}
              {lastPingTime ? (
                <Typography sx={{ fontSize: 10, opacity: 0.6 }}>
                  {dayjs(lastPingTime).fromNow()}
                </Typography>
              ) : null}
            </div>
          </div>
        }
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: 'rgba(30, 33, 40, 0.95)',
              backdropFilter: 'blur(8px)',
              padding: '8px 10px',
              maxWidth: 220,
            },
          },
          arrow: {
            sx: { color: 'rgba(30, 33, 40, 0.95)' },
          },
        }}
      >
        <div
          className={cls.dotWrapper}
          role='button'
          tabIndex={0}
          onClick={onClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          }}
        >
          {isDriving ? (
            <div
              className={cls.carWrapper}
              style={{ transform: `rotate(${headingDeg}deg)` }}
            >
              <CarIcon color={markerColor} size={isSelected ? 32 : 24} selected={isSelected} />
            </div>
          ) : (
            <>
              {isOnline ? <div className={cls.pulseRing} /> : null}
              <div
                className={cls.dot}
                style={{
                  backgroundColor: markerColor,
                  width: isSelected ? 16 : 12,
                  height: isSelected ? 16 : 12,
                  border: isSelected ? '2px solid #fff' : undefined,
                }}
              />
            </>
          )}
        </div>
      </Tooltip>
    </HtmlMapMarker>
  );
};

// STYLES -------------------------------------------------------------------- //

const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  100% {
    transform: scale(3);
    opacity: 0;
  }
`;

const useStyles = (theme?: Theme) => ({
  dotWrapper: css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    min-width: 44px;
    min-height: 44px;
  `,
  carWrapper: css`
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.4));
    transition: transform 0.3s ease-out;
    will-change: transform;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `,
  pulseRing: css`
    position: absolute;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${theme?.palette?.success?.main};
    opacity: 0.4;
    animation: ${pulseAnimation} 2s ease-out infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `,
  dot: css`
    position: relative;
    border-radius: 50%;
    border: 2px solid ${theme?.palette?.background?.paper};
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
    transition: all 0.2s ease-in-out;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `,
  tooltipContent: css`
    display: flex;
    flex-direction: column;
    gap: 6px;
  `,
  tooltipHeader: css`
    display: flex;
    align-items: center;
    gap: 8px;
  `,
  tooltipAvatar: css`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
    border: 1px solid rgba(255, 255, 255, 0.2);
    ${gpuThumbnailCss}
  `,
  tooltipAvatarFallback: css`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.15);
  `,
  tooltipInfo: css`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  `,
  tooltipStatusRow: css`
    display: flex;
    align-items: center;
    gap: 4px;
  `,
  tooltipStatusDot: css`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
  `,
  tooltipMeta: css`
    display: flex;
    gap: 8px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    padding-top: 4px;
  `,
});

export default DriverStatusMarker;
